'use client';

import { CURATOR_BROADCAST_CHANNELS, type CuratorBroadcastEvent } from '@musinsa/package-constants';
import { useBroadcastChannel } from '@musinsa/packages-common-hooks';
import { useDeviceInfo } from '@musinsa/packages-device-detection';
import { type QueryKey, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

const serializeQueryKeys = (queryKeys: QueryKey[]) =>
  queryKeys.map((queryKey) => JSON.stringify(queryKey));

const deserializeQueryKeys = (serializedKeys: string[]): QueryKey[] =>
  serializedKeys.reduce<QueryKey[]>((acc, serializedKey) => {
    try {
      const parsed = JSON.parse(serializedKey) as QueryKey;
      acc.push(parsed);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn('[CuratorBroadcast] Failed to parse query key.', {
          serializedKey,
          error,
        });
      }
    }
    return acc;
  }, []);

export const useCuratorBroadcastSubscriber = () => {
  const queryClient = useQueryClient();

  useBroadcastChannel<CuratorBroadcastEvent>(CURATOR_BROADCAST_CHANNELS.querySync, (event) => {
    if (event.type !== 'curator.query.invalidate') {
      return;
    }

    const queryKeys = deserializeQueryKeys(event.payload.queryKeys);

    queryKeys.forEach((queryKey) => {
      queryClient.invalidateQueries({ queryKey });
    });
  });
};

interface TriggerQuerySyncParams {
  queryKeys: QueryKey[];
}

export const useCuratorBroadcastPublisher = () => {
  const { isWebview } = useDeviceInfo();
  const queryClient = useQueryClient();

  const { postMessage } = useBroadcastChannel<CuratorBroadcastEvent>(
    CURATOR_BROADCAST_CHANNELS.querySync,
  );

  const triggerQuerySync = useCallback(
    ({ queryKeys }: TriggerQuerySyncParams) => {
      if (queryKeys.length === 0) {
        return;
      }

      if (isWebview) {
        postMessage({
          type: 'curator.query.invalidate',
          payload: {
            queryKeys: serializeQueryKeys(queryKeys),
          },
        });
      }

      queryKeys.forEach((queryKey) => {
        queryClient.invalidateQueries({ queryKey });
      });
    },
    [isWebview, postMessage, queryClient],
  );

  return {
    triggerQuerySync,
  } as const;
};
